import { HttpClient, HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    AddDocument, AddDocumentPermission, AddFile, AddFilePermission, BatchDocumentPermission, BatchFilePermission,
    DocumentFile,
    DocumentWithPage,
    EditDocument,
    FindListParams,
    List, MoveDocument, NodeData,
} from '@app/interfaces/dm/document';
import { UploadResult } from '@app/interfaces/lm/file';
import { environment } from '@env/environment';
import { lastValueFrom, Observable } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class DocumentService {
    private moduleApi = `/service/lamta-dm/api/v1`;
    private baseUrl = this.moduleApi + '/documents';

    constructor(private http: HttpClient) {
    }

    async find(data: FindListParams): Promise<DocumentWithPage> {
        return lastValueFrom(this.http.get <DocumentWithPage>(`${this.baseUrl}`, {
            params: Object.assign(data)
        }));
    }

    async list(): Promise<NodeData> {
        return lastValueFrom(this.http.get<NodeData>(`${this.baseUrl}/list`));
    }

    async getDocID(): Promise<any> {
        return lastValueFrom(this.http.get <any>(`${this.baseUrl}/generate-id`));
    }

    async add(data: AddDocument): Promise<void> {
        return lastValueFrom(this.http.post<void>(`${this.baseUrl}`, data));
    }

    async addFile(data: AddFile): Promise<void> {
        return lastValueFrom(this.http.post<void>(`${this.baseUrl}/add-file`, data));
    }

    async permission(data: AddDocumentPermission): Promise<void> {
        return lastValueFrom(this.http.post<void>(`${this.baseUrl}/permission`, data));
    }

    async filePermission(data: AddFilePermission): Promise<void> {
        return lastValueFrom(this.http.post<void>(`${this.baseUrl}/file-permission`, data));
    }

    async batchPermission(data: BatchDocumentPermission): Promise<void> {
        return lastValueFrom(this.http.post<void>(`${this.baseUrl}/permission-batch`, data));
    }

    async batchFilePermission(data: BatchFilePermission): Promise<void> {
        return lastValueFrom(this.http.post<void>(`${this.baseUrl}/file-permission-batch`, data));
    }

    async createLink(data: {path: string, name: string}): Promise<DocumentFile> {
        return lastValueFrom(this.http.post<DocumentFile>(`${this.moduleApi}/files/link`, data));
    }

    async edit(data: EditDocument): Promise<void> {
        return lastValueFrom(this.http.put<void>(`${this.baseUrl}`, data));
    }

    async remove(id: string): Promise<void> {
        return lastValueFrom(this.http.delete<void>(`${this.baseUrl}`, {
            params: Object.assign({ id }),
        }));
    }

    async one(data: {id?: string}): Promise<List> {
        return lastValueFrom(this.http.get <List>(`${this.baseUrl}/one`, {
            params: Object.assign(data)
        }));
    }

    uploadResponse(data: FormData): Promise<UploadResult> {
        return lastValueFrom(this.http.post<UploadResult>(`${this.moduleApi}/files`, data));
    }

    upload(data: FormData): Observable<HttpEvent<UploadResult>> {
        return this.http.post<UploadResult>(`${this.moduleApi}/files`, data, {
            reportProgress: true,
            observe: 'events'
        });
    }

    // 模拟调用接口
    async uploadFile(data: FormData): Promise<DocumentFile> {
        return new Promise<DocumentFile>(resolve => {
            setTimeout(() => {
                resolve({
                    id: '6bee7f248a9a4f55b3e76f548aa423f5',
                    // createdById: '4',
                    // createdDate: '2024-11-01T07:17:19.241464673Z',
                    // lastModifiedById: '4',
                    // lastModifiedDate: '2024-11-01T07:17:19.241464673Z',
                    active: true,
                    path: 'task/20241101071719_image.png',
                    name: '20241101071719_image.png',
                    type: 'image/png',
                    size: 1949,
                    new: false
                });
            }, 300);
        });
    }

    getPath(id: string): string {
        return `${environment.api.baseUrl}${this.moduleApi}/files?id=${id}`;
    }

    download(id: string): void {
        const url = this.getPath(id);
        window.open(url);
    }

    downloadAll(ids: string[]): void {
        const query = ids.map(id => `ids=${id}`).join('&');
        const url = `${environment.api.baseUrl}${this.moduleApi}/files/all?${query}`;
        window.open(url);
    }

    async removeFile(id: string): Promise<void> {
        return lastValueFrom(this.http.delete<void>(`${this.moduleApi}/files?id=${id}`));
    }

    async moveFile(data: MoveDocument): Promise<void> {
        return lastValueFrom(this.http.put<void>(`${this.moduleApi}/files/move`, data));
    }

    async moveFolder(data: MoveDocument): Promise<void> {
        return lastValueFrom(this.http.put<void>(`${this.baseUrl}/move`, data));
    }
}
